// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import * as ConnectionCommands from '@app/features/connection/commands/ConnectionCommands';
import type {Connection} from '@app/features/connection/models/Connection';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Switch} from '@app/features/ui/components/form/Switch';
import {ConnectionVisibilityFlags} from '@app/vendor/constants/ConnectionConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useState} from 'react';

interface Props {
	connection: Connection;
}
export const EditConnectionModal = observer(({connection}: Props) => {
	const {t, i18n} = useLingui();
	const [visibilityFlags, setVisibilityFlags] = useState(connection.visibilityFlags);
	const [submitting, setSubmitting] = useState(false);
	const hasFlag = useCallback((flag: number) => (visibilityFlags & flag) === flag, [visibilityFlags]);
	const everyoneEnabled = (visibilityFlags & ConnectionVisibilityFlags.EVERYONE) === ConnectionVisibilityFlags.EVERYONE;
	const handleToggle = useCallback(
		(flag: number, value: boolean) => {
			setVisibilityFlags((prev) => {
				if (value) {
					return prev | flag;
				}
				return prev & ~flag;
			});
		},
		[setVisibilityFlags],
	);
	const handleSave = useCallback(async () => {
		setSubmitting(true);
		try {
			await ConnectionCommands.updateConnection(i18n, connection.type, connection.id, {
				visibility_flags: visibilityFlags,
			});
			ModalCommands.pop();
		} finally {
			setSubmitting(false);
		}
	}, [i18n, connection.type, connection.id, visibilityFlags]);
	return (
		<Modal.Root size="small" centered>
			<Modal.Header title={t`Edit Connection`} />
			<Modal.Content>
				<Modal.ContentLayout>
					<Modal.Description>
						<Trans>Choose who can see this connection on your profile.</Trans>
					</Modal.Description>
					<Switch
						label={<Trans>Everyone</Trans>}
						description={<Trans>Allow anyone to see this connection on your profile</Trans>}
						value={hasFlag(ConnectionVisibilityFlags.EVERYONE)}
						onChange={(value) => handleToggle(ConnectionVisibilityFlags.EVERYONE, value)}
					/>
					<Switch
						label={<Trans>Friends</Trans>}
						description={<Trans>Allow your friends to see this connection</Trans>}
						value={everyoneEnabled || hasFlag(ConnectionVisibilityFlags.FRIENDS)}
						onChange={(value) => handleToggle(ConnectionVisibilityFlags.FRIENDS, value)}
						disabled={everyoneEnabled}
					/>
					<Switch
						label={<Trans>Community Members</Trans>}
						description={<Trans>Allow members from communities you're in to see this connection</Trans>}
						value={everyoneEnabled || hasFlag(ConnectionVisibilityFlags.MUTUAL_GUILDS)}
						onChange={(value) => handleToggle(ConnectionVisibilityFlags.MUTUAL_GUILDS, value)}
						disabled={everyoneEnabled}
					/>
				</Modal.ContentLayout>
			</Modal.Content>
			<Modal.Footer>
				<Button onClick={ModalCommands.pop} variant="secondary">
					<Trans>Cancel</Trans>
				</Button>
				<Button onClick={handleSave} submitting={submitting}>
					<Trans>Save</Trans>
				</Button>
			</Modal.Footer>
		</Modal.Root>
	);
});
